import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import Modal from '../../../../components/Modal';
import AddressAutocomplete, { AddressFields } from '../../../../components/AddressAutocomplete';
import { format } from 'date-fns';
import { notifyReadOnly } from '../../../../utils/supabaseErrors';
import './client-detail-modal.scss';

interface ClientContact {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  created_at: string;
}

type AddressType = 'street' | 'po_box' | 'email';

interface ClientDetail {
  id: number;
  company_name: string;
  address_type: AddressType | null;
  street_address: string | null;
  po_box: string | null;
  postal_address: string | null;
  header_lines: string | null;
  billing_email: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  mapbox_data: Record<string, string> | null;
  created_at: string;
  updated_at: string | null;
  contacts: ClientContact[];
}

interface ClientDetailModalProps {
  isOpen: boolean;
  clientId: number | null;
  onClose: () => void;
  onClientUpdated: () => void;
  onClientDeleted?: () => void;
  initialTab?: 'info' | 'contacts' | 'billing' | 'settings';
}

const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
  isOpen,
  clientId,
  onClose,
  onClientUpdated,
  onClientDeleted,
  initialTab = 'info'
}) => {
  const supabase = useSupabaseClient();

  const [activeTab, setActiveTab] = useState<'info' | 'contacts' | 'billing' | 'settings'>('info');
  const [clientDetail, setClientDetail] = useState<ClientDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Billing form state
  const [addressType, setAddressType] = useState<AddressType>('street');
  const [streetAddress, setStreetAddress] = useState('');
  const [postalAddress, setPostalAddress] = useState('');
  const [headerLines, setHeaderLines] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [country, setCountry] = useState('Australia');

  // Company name form state
  const [companyName, setCompanyName] = useState('');

  // Add contact inline form
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContactForm, setNewContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  // Edit contact state
  const [editingContactId, setEditingContactId] = useState<number | null>(null);
  const [editContactForm, setEditContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [editContactErrors, setEditContactErrors] = useState<Record<string, string>>({});
  const [isSavingContact, setIsSavingContact] = useState(false);

  const showSavedStatus = useCallback(() => {
    setSaveStatus('saved');
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  }, []);

  const autoSaveField = useCallback(async (fieldName: string, value: string | null) => {
    if (!clientId) return;

    setSaveStatus('saving');

    try {
      const updateData: Record<string, string | null> = {};
      updateData[fieldName] = value === '' ? null : value;

      const { data, error } = await supabase.rpc('update_client_detail', {
        p_client_id: clientId,
        client_data: updateData
      });

      if (error) throw new Error(error.message);
      if (data && !data.success) throw new Error(data.error || 'Update failed');

      showSavedStatus();
      onClientUpdated();
    } catch (err) {
      console.error('Error auto-saving client field:', err);
      setSaveStatus('error');
    }
  }, [clientId, supabase, showSavedStatus, onClientUpdated]);

  const debouncedSave = useCallback((fieldName: string, value: string | null) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      autoSaveField(fieldName, value);
    }, 600);
  }, [autoSaveField]);

  const fetchClientDetail = useCallback(async () => {
    if (!clientId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_client_detail', {
        p_client_id: clientId
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      setClientDetail(data);
      setCompanyName(data.company_name || '');
      setAddressType(data.address_type || 'street');
      setStreetAddress(data.street_address || '');
      setPostalAddress(data.postal_address || '');
      setHeaderLines(data.header_lines || '');
      setBillingEmail(data.billing_email || '');
      setCity(data.city || '');
      setState(data.state || '');
      setPostcode(data.postcode || '');
      setCountry(data.country || 'Australia');
    } catch (err) {
      console.error('Error fetching client detail:', err);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, supabase]);

  useEffect(() => {
    if (isOpen && clientId) {
      setActiveTab(initialTab);
      fetchClientDetail();
    }
  }, [isOpen, clientId, initialTab, fetchClientDetail]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    };
  }, []);

  const handleCompanyNameBlur = useCallback(() => {
    if (companyName.trim() !== (clientDetail?.company_name || '')) {
      autoSaveField('company_name', companyName.trim());
    }
  }, [companyName, clientDetail, autoSaveField]);

  // Switching address format clears the fields owned by the other formats so
  // stale data can't linger (the old PO-Box "patch" left both street_address
  // and po_box populated). City/state/postcode are kept for street ↔ PO box;
  // the email format clears the locality lines since it renders none.
  const handleAddressTypeChange = useCallback((type: AddressType) => {
    setAddressType(type);

    const patch: Record<string, string | null> = { address_type: type };
    if (type === 'street') {
      patch.postal_address = null;
      patch.billing_email = null;
      setPostalAddress('');
      setBillingEmail('');
    } else if (type === 'po_box') {
      patch.street_address = null;
      patch.billing_email = null;
      setStreetAddress('');
      setBillingEmail('');
    } else {
      // email
      patch.street_address = null;
      patch.postal_address = null;
      patch.city = null;
      patch.state = null;
      patch.postcode = null;
      setStreetAddress('');
      setPostalAddress('');
      setCity('');
      setState('');
      setPostcode('');
    }

    const saveAll = async () => {
      if (!clientId) return;
      setSaveStatus('saving');
      try {
        const { data, error } = await supabase.rpc('update_client_detail', {
          p_client_id: clientId,
          client_data: patch,
        });
        if (error) throw new Error(error.message);
        if (data && !data.success) throw new Error(data.error || 'Update failed');
        showSavedStatus();
        onClientUpdated();
      } catch (err) {
        console.error('Error changing address type:', err);
        notifyReadOnly(err);
        setSaveStatus('error');
      }
    };
    saveAll();
  }, [clientId, supabase, showSavedStatus, onClientUpdated]);

  const handlePlaceSelected = useCallback((address: AddressFields) => {
    setStreetAddress(address.street_address);
    setCity(address.city);
    setState(address.state);
    setPostcode(address.postcode);
    setCountry(address.country);
    setAddressType('street');
    const saveAll = async () => {
      if (!clientId) return;
      setSaveStatus('saving');
      try {
        const { data, error } = await supabase.rpc('update_client_detail', {
          p_client_id: clientId,
          client_data: {
            address_type: 'street',
            street_address: address.street_address || null,
            city: address.city || null,
            state: address.state || null,
            postcode: address.postcode || null,
            country: address.country || null,
            latitude: address.latitude,
            longitude: address.longitude,
            mapbox_data: address.mapbox_data
          }
        });
        if (error) throw new Error(error.message);
        if (data && !data.success) throw new Error(data.error || 'Update failed');
        showSavedStatus();
        onClientUpdated();
      } catch (err) {
        console.error('Error saving address:', err);
        notifyReadOnly(err);
        setSaveStatus('error');
      }
    };
    saveAll();
  }, [clientId, supabase, showSavedStatus, onClientUpdated]);

  const handleDelete = useCallback(async () => {
    if (!clientId) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', clientId);

      if (error) throw new Error(error.message);

      onClientDeleted?.();
      onClose();
    } catch (err) {
      console.error('Error deleting client:', err);
      notifyReadOnly(err);
      setSaveStatus('error');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [clientId, supabase, onClientDeleted, onClose]);

  const handleAddContact = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!newContactForm.first_name.trim()) errors.first_name = 'Required';
    if (!newContactForm.last_name.trim()) errors.last_name = 'Required';

    if (Object.keys(errors).length > 0) {
      setContactErrors(errors);
      return;
    }

    setIsSubmittingContact(true);
    setContactErrors({});

    try {
      const { error } = await supabase
        .from('contacts')
        .insert({
          first_name: newContactForm.first_name.trim(),
          last_name: newContactForm.last_name.trim(),
          email: newContactForm.email.trim() || null,
          phone: newContactForm.phone.trim() || null,
          client_id: clientId
        });

      if (error) throw new Error(error.message);

      setNewContactForm({ first_name: '', last_name: '', email: '', phone: '' });
      setIsAddingContact(false);
      await fetchClientDetail();
      onClientUpdated();
    } catch (err) {
      console.error('Error adding contact:', err);
      setContactErrors({ general: err instanceof Error ? err.message : 'Failed to add contact' });
    } finally {
      setIsSubmittingContact(false);
    }
  }, [newContactForm, clientId, supabase, fetchClientDetail, onClientUpdated]);

  const handleDeleteContact = useCallback(async (contactId: number) => {
    try {
      const { error } = await supabase
        .from('contacts')
        .delete()
        .eq('id', contactId);

      if (error) throw new Error(error.message);

      await fetchClientDetail();
      onClientUpdated();
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  }, [supabase, fetchClientDetail, onClientUpdated]);

  const handleStartEdit = useCallback((contact: ClientContact) => {
    setEditingContactId(contact.id);
    setEditContactForm({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email || '',
      phone: contact.phone || ''
    });
    setEditContactErrors({});
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingContactId(null);
    setEditContactForm({ first_name: '', last_name: '', email: '', phone: '' });
    setEditContactErrors({});
  }, []);

  const handleSaveContact = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!editContactForm.first_name.trim()) errors.first_name = 'Required';
    if (!editContactForm.last_name.trim()) errors.last_name = 'Required';

    if (Object.keys(errors).length > 0) {
      setEditContactErrors(errors);
      return;
    }

    if (!editingContactId) return;

    setIsSavingContact(true);
    setEditContactErrors({});

    try {
      const { error } = await supabase
        .from('contacts')
        .update({
          first_name: editContactForm.first_name.trim(),
          last_name: editContactForm.last_name.trim(),
          email: editContactForm.email.trim() || null,
          phone: editContactForm.phone.trim() || null
        })
        .eq('id', editingContactId);

      if (error) throw new Error(error.message);

      setEditingContactId(null);
      setEditContactForm({ first_name: '', last_name: '', email: '', phone: '' });
      await fetchClientDetail();
      onClientUpdated();
    } catch (err) {
      console.error('Error updating contact:', err);
      setEditContactErrors({ general: err instanceof Error ? err.message : 'Failed to update contact' });
    } finally {
      setIsSavingContact(false);
    }
  }, [editContactForm, editingContactId, supabase, fetchClientDetail, onClientUpdated]);

  const headerStatusContent = useMemo(() => {
    switch (saveStatus) {
      case 'saving':
        return <span className="save-status save-status--saving">Saving...</span>;
      case 'saved':
        return <span className="save-status save-status--saved">All changes saved</span>;
      case 'error':
        return <span className="save-status save-status--error">Save failed</span>;
      default:
        return null;
    }
  }, [saveStatus]);

  if (!isOpen) return null;

  const modalTitle = clientDetail ? clientDetail.company_name : 'Loading...';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="md"
      hasTabs={true}
      noPadding={true}
      hasFooter={false}
      scrollable={true}
      headerStatus={headerStatusContent}
    >
      <div className="client-detail-modal">
        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            Client Info
          </button>
          <button
            className={`tab-button ${activeTab === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('contacts')}
          >
            Contacts
          </button>
          <button
            className={`tab-button ${activeTab === 'billing' ? 'active' : ''}`}
            onClick={() => setActiveTab('billing')}
          >
            Billing
          </button>
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading-message">Loading client details...</div>
          ) : clientDetail ? (
            <form onSubmit={(e) => e.preventDefault()} className="client-detail-form">

              {activeTab === 'info' && (
                <div className="info-tab">
                  <div className="form-section">
                    <h4 className="section-heading">General</h4>
                    <div className="form-fields-vertical">
                      <div className="form-field-row">
                        <label className="field-label">Company Name</label>
                        <div className="field-input">
                          <input
                            type="text"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            onBlur={handleCompanyNameBlur}
                            placeholder="Company name"
                          />
                        </div>
                      </div>
                      <div className="form-field-row">
                        <label className="field-label">Created</label>
                        <div className="field-value">
                          {clientDetail.created_at
                            ? format(new Date(clientDetail.created_at), 'MMM d, yyyy')
                            : '-'}
                        </div>
                      </div>
                      {clientDetail.updated_at && (
                        <div className="form-field-row">
                          <label className="field-label">Last Updated</label>
                          <div className="field-value">
                            {format(new Date(clientDetail.updated_at), 'MMM d, yyyy')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-section">
                    <h4 className="section-heading">Summary</h4>
                    <div className="form-fields-inline two-col">
                      <div className="form-field">
                        <label className="field-label">Contacts</label>
                        <div className="field-value">{clientDetail.contacts.length}</div>
                      </div>
                      <div className="form-field">
                        <label className="field-label">Address</label>
                        <div className="field-value">
                          {clientDetail.city && clientDetail.state
                            ? `${clientDetail.city}, ${clientDetail.state}`
                            : 'Not set'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'contacts' && (
                <div className="contacts-tab">
                  <div className="contacts-header">
                    <h4 className="section-heading">
                      Contacts ({clientDetail.contacts.length})
                    </h4>
                    {!isAddingContact && (
                      <button
                        type="button"
                        className="btn-add-inline"
                        onClick={() => setIsAddingContact(true)}
                      >
                        + Add Contact
                      </button>
                    )}
                  </div>

                  {isAddingContact && (
                    <div className="add-contact-inline">
                      {contactErrors.general && (
                        <div className="error-message">{contactErrors.general}</div>
                      )}
                      <div className="inline-form-row">
                        <div className="inline-field">
                          <input
                            type="text"
                            value={newContactForm.first_name}
                            onChange={(e) => setNewContactForm(p => ({ ...p, first_name: e.target.value }))}
                            placeholder="First name *"
                            className={contactErrors.first_name ? 'error' : ''}
                          />
                        </div>
                        <div className="inline-field">
                          <input
                            type="text"
                            value={newContactForm.last_name}
                            onChange={(e) => setNewContactForm(p => ({ ...p, last_name: e.target.value }))}
                            placeholder="Last name *"
                            className={contactErrors.last_name ? 'error' : ''}
                          />
                        </div>
                      </div>
                      <div className="inline-form-row">
                        <div className="inline-field">
                          <input
                            type="email"
                            value={newContactForm.email}
                            onChange={(e) => setNewContactForm(p => ({ ...p, email: e.target.value }))}
                            placeholder="Email"
                          />
                        </div>
                        <div className="inline-field">
                          <input
                            type="tel"
                            value={newContactForm.phone}
                            onChange={(e) => setNewContactForm(p => ({ ...p, phone: e.target.value }))}
                            placeholder="Phone"
                          />
                        </div>
                      </div>
                      <div className="inline-form-actions">
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          onClick={handleAddContact}
                          disabled={isSubmittingContact}
                        >
                          {isSubmittingContact ? 'Adding...' : 'Add'}
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => {
                            setIsAddingContact(false);
                            setNewContactForm({ first_name: '', last_name: '', email: '', phone: '' });
                            setContactErrors({});
                          }}
                          disabled={isSubmittingContact}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {clientDetail.contacts.length === 0 && !isAddingContact ? (
                    <div className="empty-state">
                      No contacts yet. Add a contact to get started.
                    </div>
                  ) : (
                    <div className="contacts-list">
                      {clientDetail.contacts.map(contact => (
                        editingContactId === contact.id ? (
                          <div key={contact.id} className="contact-card editing">
                            {editContactErrors.general && (
                              <div className="error-message">{editContactErrors.general}</div>
                            )}
                            <div className="inline-form-row">
                              <div className="inline-field">
                                <input
                                  type="text"
                                  value={editContactForm.first_name}
                                  onChange={(e) => setEditContactForm(p => ({ ...p, first_name: e.target.value }))}
                                  placeholder="First name *"
                                  className={editContactErrors.first_name ? 'error' : ''}
                                />
                              </div>
                              <div className="inline-field">
                                <input
                                  type="text"
                                  value={editContactForm.last_name}
                                  onChange={(e) => setEditContactForm(p => ({ ...p, last_name: e.target.value }))}
                                  placeholder="Last name *"
                                  className={editContactErrors.last_name ? 'error' : ''}
                                />
                              </div>
                            </div>
                            <div className="inline-form-row">
                              <div className="inline-field">
                                <input
                                  type="email"
                                  value={editContactForm.email}
                                  onChange={(e) => setEditContactForm(p => ({ ...p, email: e.target.value }))}
                                  placeholder="Email"
                                />
                              </div>
                              <div className="inline-field">
                                <input
                                  type="tel"
                                  value={editContactForm.phone}
                                  onChange={(e) => setEditContactForm(p => ({ ...p, phone: e.target.value }))}
                                  placeholder="Phone"
                                />
                              </div>
                            </div>
                            <div className="inline-form-actions">
                              <button
                                type="button"
                                className="btn-primary btn-sm"
                                onClick={handleSaveContact}
                                disabled={isSavingContact}
                              >
                                {isSavingContact ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                onClick={handleCancelEdit}
                                disabled={isSavingContact}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div key={contact.id} className="contact-card">
                            <div className="contact-info">
                              <div className="contact-name">
                                {contact.first_name} {contact.last_name}
                              </div>
                              <div className="contact-details">
                                {contact.email && <span>{contact.email}</span>}
                                {contact.email && contact.phone && <span className="separator">|</span>}
                                {contact.phone && <span>{contact.phone}</span>}
                              </div>
                            </div>
                            <div className="contact-actions">
                              <button
                                type="button"
                                className="btn-edit-contact"
                                onClick={() => handleStartEdit(contact)}
                                title="Edit contact"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn-delete-contact"
                                onClick={() => handleDeleteContact(contact.id)}
                                title="Remove contact"
                              >
                                &times;
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'billing' && (
                <div className="billing-tab">
                  <div className="form-section">
                    <h4 className="section-heading">Billing Address</h4>

                    <div className="form-fields-inline one-col">
                      <div className="form-field">
                        <label className="field-label">Header Lines</label>
                        <div className="field-input">
                          <textarea
                            value={headerLines}
                            onChange={(e) => {
                              setHeaderLines(e.target.value);
                              debouncedSave('header_lines', e.target.value);
                            }}
                            placeholder="Optional lines shown above the company name, one per line (e.g. department / attention)"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="address-type-toggle">
                      <button
                        type="button"
                        className={`toggle-btn ${addressType === 'street' ? 'active' : ''}`}
                        onClick={() => handleAddressTypeChange('street')}
                      >
                        Street Address
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${addressType === 'po_box' ? 'active' : ''}`}
                        onClick={() => handleAddressTypeChange('po_box')}
                      >
                        PO Box
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${addressType === 'email' ? 'active' : ''}`}
                        onClick={() => handleAddressTypeChange('email')}
                      >
                        Via Email
                      </button>
                    </div>

                    {addressType === 'street' && (
                      <div className="form-fields-inline one-col">
                        <div className="form-field">
                          <label className="field-label">Street Address</label>
                          <div className="field-input">
                            <AddressAutocomplete
                              onPlaceSelected={handlePlaceSelected}
                              value={streetAddress}
                              onChange={(val) => {
                                setStreetAddress(val);
                                debouncedSave('street_address', val);
                              }}
                              placeholder="Search address..."
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {addressType === 'po_box' && (
                      <div className="form-fields-inline one-col">
                        <div className="form-field">
                          <label className="field-label">Postal Address</label>
                          <div className="field-input">
                            <textarea
                              value={postalAddress}
                              onChange={(e) => {
                                setPostalAddress(e.target.value);
                                debouncedSave('postal_address', e.target.value);
                              }}
                              placeholder="e.g. PO Box 793  or  Locked Bag 44, Cloisters Square"
                              rows={2}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {addressType === 'email' && (
                      <div className="form-fields-inline one-col">
                        <div className="form-field">
                          <label className="field-label">Billing Email</label>
                          <div className="field-input">
                            <input
                              type="email"
                              value={billingEmail}
                              onChange={(e) => {
                                setBillingEmail(e.target.value);
                                debouncedSave('billing_email', e.target.value);
                              }}
                              placeholder="accounts@example.com"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {addressType !== 'email' && (
                      <>
                        <div className="form-fields-inline two-col">
                          <div className="form-field">
                            <label className="field-label">City / Suburb</label>
                            <div className="field-input">
                              <input
                                type="text"
                                value={city}
                                onChange={(e) => {
                                  setCity(e.target.value);
                                  debouncedSave('city', e.target.value);
                                }}
                                placeholder="City"
                              />
                            </div>
                          </div>
                          <div className="form-field">
                            <label className="field-label">State</label>
                            <div className="field-input">
                              <input
                                type="text"
                                value={state}
                                onChange={(e) => {
                                  setState(e.target.value);
                                  debouncedSave('state', e.target.value);
                                }}
                                placeholder="State"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="form-fields-inline two-col">
                          <div className="form-field">
                            <label className="field-label">Postcode</label>
                            <div className="field-input">
                              <input
                                type="text"
                                value={postcode}
                                onChange={(e) => {
                                  setPostcode(e.target.value);
                                  debouncedSave('postcode', e.target.value);
                                }}
                                placeholder="Postcode"
                              />
                            </div>
                          </div>
                          <div className="form-field">
                            <label className="field-label">Country</label>
                            <div className="field-input">
                              <input
                                type="text"
                                value={country}
                                onChange={(e) => {
                                  setCountry(e.target.value);
                                  debouncedSave('country', e.target.value);
                                }}
                                placeholder="Country"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="settings-tab">
                  <div className="settings-section">
                    <h3>Client Information</h3>
                    <div className="form-fields-vertical">
                      <div className="form-field-row">
                        <label className="field-label">Client ID</label>
                        <div className="field-value">{clientDetail.id}</div>
                      </div>
                      <div className="form-field-row">
                        <label className="field-label">Created</label>
                        <div className="field-value">
                          {clientDetail.created_at
                            ? format(new Date(clientDetail.created_at), 'MMM d, yyyy')
                            : '-'}
                        </div>
                      </div>
                      <div className="form-field-row">
                        <label className="field-label">Contacts</label>
                        <div className="field-value">{clientDetail.contacts.length}</div>
                      </div>
                    </div>
                  </div>

                  <div className="danger-zone-section">
                    <h3>Danger Zone</h3>
                    <p className="action-description">
                      Deleting this client will permanently remove it and all associated contacts. This action cannot be undone.
                    </p>
                    <button
                      type="button"
                      className="btn-danger action-button"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting || saveStatus === 'saving'}
                    >
                      Delete Client
                    </button>
                  </div>
                </div>
              )}
            </form>
          ) : (
            <div className="loading-message">Client not found.</div>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-modal">
              <h3>Confirm Delete</h3>
              <p>
                Are you sure you want to delete this client? This will also remove all associated contacts. This action cannot be undone.
              </p>
              <p className="project-name">
                <strong>{clientDetail?.company_name}</strong>
              </p>
              <div className="confirm-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Client'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ClientDetailModal;
